import {NextIcon, PrevIcon} from "@components/icons";
import {useDownBreakpoint} from "@hooks";
import {Button, Dialog, IconButton, Stack, Typography} from "@mui/material";
import {DateView, StaticDatePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";
import {useState} from "react";
import {NavigateAction, View} from "react-big-calendar";

const Navigator = ({
  view,
  date,
  disabled,
  onNavigate,
}: {
  view: View;
  date: Date;
  disabled?: boolean;
  onNavigate: (navigate: NavigateAction, date?: Date) => void;
}) => {
  const isDownSm = useDownBreakpoint("sm");
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);

  const getLabelWidth = () => {
    switch (view) {
      case "month":
      case "agenda":
        return isDownSm ? 80 : 150;
      case "week":
        return isDownSm ? 100 : 160;
      case "day":
        return isDownSm ? 80 : 150;
      default:
        return 150;
    }
  };

  const formatWeek = (date: Date) => {
    const d = dayjs(date);
    const start = d.startOf("week");
    const end = d.endOf("week");
    return `${start.format("D")} - ${end.format("D")} ${d.format(isDownSm ? "MMM" : "MMMM")}`;
  };

  const formatDate = () => {
    const d = dayjs(date);
    const format = isDownSm ? "D MMM" : "D MMMM";
    switch (view) {
      case "month":
      case "agenda":
        return d.format(isDownSm ? "MMMM" : "MMMM YYYY");
      case "week":
        return formatWeek(date);
      case "day":
        return d.format(format);
      default:
        return "";
    }
  };

  const getDatePickerViews = (): DateView[] => {
    switch (view) {
      case "month":
        return ["year", "month"];
      case "agenda":
      case "week":
        return ["year", "month", "day"];
      case "day":
        return ["year", "month", "day"];
      default:
        return ["year", "month"];
    }
  };

  const handleAcceptDate = (newValue: Dayjs | null) => {
    if (newValue) {
      onNavigate("DATE", newValue.toDate());
    }
    setPickerOpen(false);
  };

  const handleLabelClick = () => {
    setPickerOpen(!pickerOpen);
  };

  return (
    <>
      <Stack
        direction="row"
        gap={isDownSm ? 0 : 1}
        alignItems="center"
        justifyContent={"center"}
        flex={1}>
        <IconButton
          onClick={() => onNavigate("PREV")}
          size="medium"
          disabled={disabled}>
          <PrevIcon fontSize="inherit" />
        </IconButton>
        <Button
          onClick={handleLabelClick}
          size="small"
          sx={{width: getLabelWidth(), px: 1, py: 1}}
          disabled={disabled}>
          <Typography
            variant={isDownSm ? "body2" : "body1"}
            textTransform={"capitalize"}>
            {formatDate()}
          </Typography>
        </Button>
        <IconButton
          onClick={() => onNavigate("NEXT")}
          size="medium"
          disabled={disabled}>
          <NextIcon fontSize="inherit" />
        </IconButton>
      </Stack>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)}>
        <StaticDatePicker
          displayStaticWrapperAs={"desktop"}
          fixedWeekNumber={6}
          showDaysOutsideCurrentMonth
          views={getDatePickerViews()}
          onAccept={handleAcceptDate}
          slotProps={{actionBar: {actions: ["nextOrAccept"]}}}
        />
      </Dialog>
    </>
  );
};

export {Navigator};
